"use client";

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

export default function MentorsSlider({ individualMentors = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const cardsToShow = isDesktop ? 4 : 1;
  const maxIndex = Math.max(0, individualMentors.length - cardsToShow);

  const nextSlide = () => {
    setCurrentIndex(prev => prev < maxIndex ? prev + 1 : prev);
  };

  const prevSlide = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : 0);
  };

  // Mobile drag handlers
  const handleMouseDown = (e) => {
    if (isDesktop) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleTouchStart = (e) => {
    if (isDesktop) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || isDesktop) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    const newScrollLeft = scrollLeft - walk;
    
    // Calculate max scroll with proper gap consideration
    const cardWidth = 224 + 24; // Card width + gap
    const containerWidth = sliderRef.current.clientWidth;
    const totalCards = individualMentors.length;
    const maxScroll = Math.max(0, (totalCards * cardWidth) - containerWidth);
    
    const clampedScroll = Math.max(0, Math.min(newScrollLeft, maxScroll));
    
    sliderRef.current.scrollLeft = clampedScroll;
  };

  const handleTouchMove = (e) => {
    if (!isDragging || isDesktop) return;
    const x = e.touches[0].pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    const newScrollLeft = scrollLeft - walk;
    
    // Calculate max scroll with proper gap consideration
    const cardWidth = 224 + 24; // Card width + gap
    const containerWidth = sliderRef.current.clientWidth;
    const totalCards = individualMentors.length;
    const maxScroll = Math.max(0, (totalCards * cardWidth) - containerWidth);
    
    const clampedScroll = Math.max(0, Math.min(newScrollLeft, maxScroll));
    
    sliderRef.current.scrollLeft = clampedScroll;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || isDesktop) return;

    slider.addEventListener('mousedown', handleMouseDown);
    slider.addEventListener('mousemove', handleMouseMove);
    slider.addEventListener('mouseup', handleMouseUp);
    slider.addEventListener('mouseleave', handleMouseUp);
    
    slider.addEventListener('touchstart', handleTouchStart);
    slider.addEventListener('touchmove', handleTouchMove);
    slider.addEventListener('touchend', handleTouchEnd);

    return () => {
      slider.removeEventListener('mousedown', handleMouseDown);
      slider.removeEventListener('mousemove', handleMouseMove);
      slider.removeEventListener('mouseup', handleMouseUp);
      slider.removeEventListener('mouseleave', handleMouseUp);
      
      slider.removeEventListener('touchstart', handleTouchStart);
      slider.removeEventListener('touchmove', handleTouchMove);
      slider.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, isDesktop, scrollLeft, startX]);

  if (!individualMentors || individualMentors.length === 0) {
    return (
      <section className="py-16">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <p className="text-center text-gray-600">Loading mentors...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16">
      {/* Header Container - 80% desktop, 90% mobile */}
      <div className="w-[90%] md:w-[80%] mx-auto mb-12">
        <p className="text-gray-600 text-sm uppercase mb-2 font-medium">EXPERT GUIDANCE</p>
        <div className="flex items-center justify-between">
          <h2 className="text-3xl md:text-4xl font-medium text-gray-900">
            Mentors from top brands
          </h2>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {/* <button className="text-gray-400 hover:text-gray-600 underline text-sm">
              See All
            </button> */}
            <div className="flex space-x-2">
              <button
                onClick={prevSlide}
                className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slider Container - Full width */}
      <div className="w-full overflow-hidden">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <div 
            ref={sliderRef}
            className={`flex transition-transform duration-300 ease-in-out ${!isDesktop ? 'overflow-x-auto scrollbar-hide cursor-grab' : ''} ${isDragging ? 'cursor-grabbing' : ''}`}
            style={{
              transform: isDesktop ? `translateX(-${currentIndex * (100 / cardsToShow)}%)` : 'none',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {individualMentors.map((mentor, index) => (
              <div
                key={mentor.mentorId}
                className="flex-shrink-0 flex justify-center"
                style={{ 
                  width: isDesktop ? '18%' : '224px',
                  marginRight: index === individualMentors.length - 1 ? '0' : '20px'
                }}
              >
                <div className="bg-white rounded-2xl p-5 border border-gray-200 h-full flex flex-col" style={{ width: '224px', minHeight: '330px' }}>
                  {/* Mentor Image - 152x152 */}
                  <div className="relative w-[182px] h-[172px] mb-4 mx-auto overflow-hidden">
                    <Image
                      src={mentor.mainImage}
                      alt={mentor.name}
                      fill
                      className="object-cover border border-black/30 rounded-md"
                    />
                  </div>

                  {/* Mentor Info - Left aligned */}
                  <div className="flex-grow flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 text-left">
                      {mentor.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 text-left">
                      {mentor.designation}
                    </p>
                    
                    {/* Company Name at Bottom - Left aligned */}
                    <div className="mt-auto">
                      <p className="text-gray-800 text-base font-medium text-left">
                        {mentor.companyName}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>


    </section>
  );
}